import { debounce } from 'lodash-es';
import type { Delta, EmitterSource } from 'quill/core';
import Quill from 'quill/core';
import { useCallback, useLayoutEffect, useState } from 'react';
import { TYPING_DEBOUNCE_TIMEOUT } from './constants';
import { queryMagneticWallElementsWithIds } from './MagneticWall';
import { useOptimisticItemContentRects } from './useOptimisticItemContentRects';
import { useQuill } from '../editor/QuillContext';
import type { DocumentType } from '../graphql/generated';
import { gql } from '../graphql/generated';

const POST_FRAGMENT = gql(`
    fragment useHasMagneticWallItems_post on PostFields {
        id
        threads {
            id
        }
        annotations {
            id
        }
    }
`);

const DOM_CHANGE_DEBOUNCE_TIMEOUT = 100;

const containsQuillEditor = (node: Node) => {
  const quillSelector = '.ql-container';
  return (
    node instanceof Element &&
    (node.matches(quillSelector) || node.querySelector(quillSelector))
  );
};

const useHasMagneticWallItems = (
  post: DocumentType<typeof POST_FRAGMENT> | undefined,
  skip?: boolean,
) => {
  const { quill } = useQuill();

  const [hasMagneticWallItems, setHasMagneticWallItems] = useState(false);
  const optimisticItemContentRects = useOptimisticItemContentRects();
  const hasOptimisticItemContentRects = optimisticItemContentRects.size > 0;

  const checkAndSetMagneticWallItemState = useCallback(() => {
    const hasThreadsOrAnnotations =
      (!!post?.threads && post.threads.length > 0) ||
      (!!post?.annotations && post.annotations.length > 0);

    const hasElements =
      quill && hasThreadsOrAnnotations
        ? queryMagneticWallElementsWithIds(quill.container).length > 0
        : false;

    setHasMagneticWallItems(hasElements || hasOptimisticItemContentRects);
  }, [quill, post?.threads, post?.annotations, hasOptimisticItemContentRects]);

  useLayoutEffect(() => {
    if (!quill) return undefined;

    if (skip) {
      setHasMagneticWallItems(false);
      return undefined;
    }

    const debouncedCheckOnTextChange = debounce(
      checkAndSetMagneticWallItemState,
      TYPING_DEBOUNCE_TIMEOUT,
    );

    const debouncedCheckOnDOMChange = debounce(
      checkAndSetMagneticWallItemState,
      DOM_CHANGE_DEBOUNCE_TIMEOUT,
    );

    checkAndSetMagneticWallItemState();

    // Nested Quill editors (e.g. table cells or code blocks) render content asynchronously.
    // Watch for DOM changes to recheck for magnetic wall items.
    const editorDOMObserver = new MutationObserver((mutations) => {
      if (
        mutations.some((mutation) => {
          return [...mutation.addedNodes].some(containsQuillEditor);
        })
      ) {
        debouncedCheckOnDOMChange();
      }
    });

    editorDOMObserver.observe(quill.scroll.domNode, {
      childList: true,
      subtree: true,
    });

    const onTextChange = (
      _delta: Delta,
      _old: Delta,
      source: EmitterSource,
    ) => {
      if (source === 'user') {
        debouncedCheckOnTextChange();
        editorDOMObserver.disconnect(); // No need to further observe DOM changes
      }
    };

    quill.on(Quill.events.TEXT_CHANGE, onTextChange);

    return () => {
      editorDOMObserver.disconnect();
      quill.off(Quill.events.TEXT_CHANGE, onTextChange);
      debouncedCheckOnTextChange.cancel();
      debouncedCheckOnDOMChange.cancel();
    };
  }, [quill, checkAndSetMagneticWallItemState, skip]);

  return hasMagneticWallItems;
};

export default useHasMagneticWallItems;
